import { Colors, Icon } from "@blueprintjs/core";

import { pluralize } from "@/components/helpers/string-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { MIDTEXT_COLOR, SUBTEXT_COLOR } from "@/css/constants";
import { QuestionField } from "@/types/surveys";
import { ensureNonNullArray } from "@/utils/array";

export default function QuestionStatistic({
  question,
}: {
  question: QuestionField;
}) {
  const { average, favorable, options, totalResponses } = question;
  const colors = ["#C61D1D", "#F5E6E8", "#F0F0F0", "#E0F0E7", "#129B4C"];
  const borderColors = ["#C61D1D", "#DD8183", "#A6A6A6", "#79C699", "#129B4C"];

  const cleanOptions = ensureNonNullArray(options);

  const statisticData = cleanOptions
    .map((item) => {
      const value = parseInt(item.value) || 1;
      const colorIndex = (value - 1) % 5;
      return {
        value: value,
        label: item.label,
        color: colors?.[colorIndex] || colors[0],
        count: item.totalResponses,
        borderColor: borderColors?.[colorIndex] || borderColors[0],
        percent: item.valueProportion
          ? (item.valueProportion * 100)?.toFixed(2)?.replace(/\.0+$/, "")
          : 0,
      };
    })
    .sort((a, b) => a.value - b.value);

  const favorablePercent = favorable ? (favorable * 100).toFixed(2) : 0;

  return (
    <Box flex={1} mt={[2, 36]}>
      <Box className="bbs" my={24} display={["flex", "none"]} />
      <Flex mb={16} flexDirection={["column", "row"]}>
        <Flex
          alignItems={"center"}
          mr={[0, 24]}
          mb={[2, 0]}
          color={SUBTEXT_COLOR}
        >
          <Box fontSize={18} fontWeight={600} mr={"5px"} color={Colors.BLACK}>
            {totalResponses}
          </Box>
          {pluralize({
            amount: totalResponses,
            singular: "response",
            plural: "responses",
            showCount: false,
          })}
        </Flex>
        <Flex
          alignItems={"center"}
          mr={[0, 24]}
          mb={[2, 0]}
          color={SUBTEXT_COLOR}
        >
          <Box fontSize={18} fontWeight={600} mr={"5px"} color={Colors.BLACK}>
            {average?.toFixed(2)?.replace(/\.0+$/, "")}
          </Box>
          average
        </Flex>
        <Flex alignItems={"center"} color={SUBTEXT_COLOR}>
          <Icon icon="thumbs-up" color={Colors.BLACK} />
          <Box fontSize={18} fontWeight={600} mx={"5px"} color={Colors.BLACK}>
            {favorablePercent}%
          </Box>
          favorable
        </Flex>
      </Flex>
      <Box className="bbs" my={24} display={["flex", "none"]} />
      {!!totalResponses && (
        <Box mb={36} flex={1} height={12} width={1} display={["none", "flex"]}>
          {statisticData
            .filter((item) => (item.count ?? 0) > 0)
            .map((item, index) => (
              <Flex
                key={item.label}
                flex={item.count ?? 0}
                justifyContent={"center"}
                css={
                  index !== statisticData.length - 1 ? { marginRight: 5 } : null
                }
              >
                <Box
                  width={1}
                  backgroundColor={item.color}
                  height={12}
                  css={{
                    border: `solid 1px ${item.borderColor}`,
                    borderRadius: 4,
                  }}
                ></Box>
              </Flex>
            ))}
        </Box>
      )}

      <Flex flex={1} flexDirection={["column", "row"]} flexWrap={"wrap"}>
        {statisticData.map((item) => (
          <Box
            key={item.label}
            mr={[0, 24]}
            mb={[2, 0]}
            justifyContent={"center"}
          >
            <Box color={MIDTEXT_COLOR} fontWeight={400}>
              {item.label}
            </Box>
            <Flex alignItems={"center"}>
              <Box
                backgroundColor={item.color}
                width={12}
                height={12}
                css={{
                  border: `solid 1px ${item.borderColor}`,
                  borderRadius: 4,
                }}
              ></Box>
              <Box fontSize={18} fontWeight={600} ml={"5px"}>
                {item.count}
              </Box>
              <Box
                fontSize={16}
                fontWeight={400}
                ml={"10px"}
                color={SUBTEXT_COLOR}
              >
                {item.percent}%
              </Box>
            </Flex>
          </Box>
        ))}
      </Flex>
    </Box>
  );
}
